/**
 * Safe application of a recurring-message edit plan (FE#151).
 *
 * `RRuleRecurrenceHandler.handleRecurringEdit` decides what should happen to the original
 * occurrence and whether a replacement is needed. Applying that plan is destructive, so the
 * ORDER matters:
 *
 *   create the replacement -> confirm it -> only then remove/patch the original
 *
 * The previous implementation dispatched the delete and the create as two independent,
 * unawaited calls. A create that failed for any reason (validation, network, a rejected
 * paywall check) left the customer with neither the original nor the replacement — silent,
 * unrecoverable loss of a scheduled message during a routine edit.
 *
 * Trade-off, deliberately chosen: creating first means original and replacement briefly
 * coexist. A same-instant dispatch could in principle see both, and that risk is bounded by
 * the send-protocol store-check. Losing the customer's message outright is the worse
 * failure, so we prefer the recoverable one.
 */

export interface RecurringEditPlan {
  originalAction?: 'delete' | 'patch' | string | null;
  newAction?: 'create' | string | null;
}

export interface RecurringEditEffects {
  /** Persist the replacement occurrence. Must reject if it did not store. */
  createReplacement: () => Promise<unknown>;
  /** Remove the original occurrence. */
  deleteOriginal: () => Promise<unknown>;
  /** Rewrite the original occurrence's rule/date in place. */
  patchOriginal: () => Promise<unknown>;
}

/**
 * Applies `plan` via `effects`, guaranteeing the original is never removed unless the
 * replacement it is being traded for was stored successfully.
 *
 * @throws whatever `createReplacement` rejects with, having performed no destructive work.
 */
export async function applyRecurringEditPlan(
  plan: RecurringEditPlan,
  effects: RecurringEditEffects,
): Promise<void> {
  const needsReplacement = plan.newAction === 'create';
  const removesOriginal = plan.originalAction === 'delete';
  const rewritesOriginal = plan.originalAction === 'patch';

  // Create first. If this throws, nothing destructive has happened and the customer still
  // has exactly what they had before the edit.
  if (needsReplacement) {
    await effects.createReplacement();
  }

  if (removesOriginal) {
    await effects.deleteOriginal();
  } else if (rewritesOriginal) {
    await effects.patchOriginal();
  }
}
